import * as React from 'react';
import type { InferableComponentEnhancerWithProps } from 'react-redux';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { HTTP } from '@helpcenter/constants';
import { ErrorPage } from '@helpcenter/error-page';
import { Loading } from '@helpcenter/loading';
import { toNumber, toSafeInteger } from 'lodash';
import { di } from 'react-magnetic-di';
import type { RouteContext } from 'react-resource-router';
import { usePathParam } from 'react-resource-router';
import type { State } from 'state';
import { fetchPortalAction } from 'state/actions/portal';
import { getCanAdministerProject } from 'state/selectors/portal';
import { BannerWithPaperLayoutRoute } from 'view/layout/banner-with-paper-layout';
import type { PersistedError } from 'state/persisted/types';

type ExternalProps = Pick<RouteContext, 'match'>;

const selector = createSelector(
	(state: State, props: ExternalProps) => {
		di(getCanAdministerProject);
		const portalIdAsString = props.match.params && props.match.params.portalId;
		if (portalIdAsString) {
			return getCanAdministerProject(state, toNumber(portalIdAsString));
		}
		return false;
	},
	(isProjectAdmin) => ({
		isProjectAdmin,
	}),
);

type ConnectedProps<T> =
	T extends InferableComponentEnhancerWithProps<infer Props, infer _> ? Props : never;

const connector = connect(selector, {
	fetchPortals: fetchPortalAction,
});

export type OnlyAdminOption = Omit<PersistedError['error'], 'status'> & { hideBanner: boolean };

const withOnlyProjectAdmin =
	(options: OnlyAdminOption) =>
	<TProps extends object>(WrappedComponent: React.ComponentType<TProps>) => {
		const ReturnComponent: React.FC<TProps & ConnectedProps<typeof connector>> = (props) => {
			di(BannerWithPaperLayoutRoute, usePathParam, Loading);
			const { isProjectAdmin, fetchPortals } = props;
			const [portalId] = usePathParam('portalId');

			React.useEffect(() => {
				if (isProjectAdmin === undefined && options.hideBanner) {
					fetchPortals({
						id: toSafeInteger(portalId),
						expand: ['kbs', 'reqTypes', 'reqGroups', 'orderMapping'],
					});
				}
			}, [isProjectAdmin, fetchPortals, portalId]);

			if (isProjectAdmin === undefined) {
				return options.hideBanner ? (
					<Loading />
				) : (
					<BannerWithPaperLayoutRoute
						component={() => <Loading />}
						layoutProps={{
							headerless: false,
							fadeInSearchModal: true,
						}}
					/>
				);
			}
			if (isProjectAdmin) {
				//@ts-ignore TypeScript upgrade 4.7.4
				return <WrappedComponent {...props} />;
			}
			const { message, callToActionUrl, callToActionText } = options;

			return (
				<BannerWithPaperLayoutRoute
					component={() => (
						<ErrorPage
							error={{
								message,
								callToActionUrl,
								callToActionText,
								status: HTTP.FORBIDDEN,
							}}
						/>
					)}
					layoutProps={{
						headerless: false,
						paperAppearance: 'normal',
						fadeInSearchModal: true,
					}}
				/>
			);
		};

		// @ts-ignore
		const ConnectedComponent = connector(ReturnComponent);
		ConnectedComponent.displayName = 'withOnlyProjectAdmin';

		return ConnectedComponent;
	};

export default withOnlyProjectAdmin;
